import { useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { getDB } from '../utils/indexedDb'
import { useOnlineStatus } from './useOnlineStatus'
import { useAuthStore } from '../stores/authStore'

/**
 * Hook do synchronizacji danych kalendarza między IndexedDB a Supabase
 */
export function useSyncCalendar() {
  const isOnline = useOnlineStatus()
  const { user } = useAuthStore()

  // Funkcja do synchronizacji danych z IndexedDB do Supabase
  const syncToServer = useCallback(async () => {
    if (!isOnline || !user) return

    console.log('🔄 Rozpoczynam synchronizację danych do serwera...')
    
    try {
      const db = await getDB()
      
      // Pobierz wszystkie zmiany oczekujące na synchronizację
      const pendingChanges = await db.getAllFromIndex('pending_changes', 'by-timestamp')
      
      if (pendingChanges.length === 0) {
        console.log('✅ Brak zmian do synchronizacji')
        return
      }
      
      console.log(`🔄 Znaleziono ${pendingChanges.length} zmian do synchronizacji`)
      
      // Przetwarzaj zmiany po kolei
      for (const change of pendingChanges) {
        try {
          if (change.table === 'calendar_entries') {
            switch (change.action) {
              case 'create': {
                // Usuń tymczasowe ID i dodaj wpis do Supabase
                const { id, sync_status, ...entryData } = change.data
                
                const { data, error } = await supabase
                  .from('calendar_entries')
                  .insert(entryData)
                  .select()
                
                if (error) throw error
                
                // Zaktualizuj wpis w IndexedDB z nowym ID z serwera
                if (data && data.length > 0) {
                  // Usuń stary wpis z tymczasowym ID
                  await db.delete('calendar_entries', id)
                  
                  // Dodaj nowy wpis z ID z serwera
                  await db.put('calendar_entries', {
                    ...data[0],
                    sync_status: 'synced'
                  })
                  
                  console.log(`✅ Wpis zsynchronizowany: ${id} -> ${data[0].id}`)
                }
                break
              }
              
              case 'update': {
                const { id, sync_status, ...updateData } = change.data
                
                // Jeśli ID zaczyna się od 'offline-', to ten wpis nie został jeszcze dodany do serwera
                if (id.startsWith('offline-')) {
                  // Traktuj to jako 'create'
                  const { data, error } = await supabase
                    .from('calendar_entries')
                    .insert(updateData)
                    .select()
                  
                  if (error) throw error
                  
                  // Zaktualizuj wpis w IndexedDB
                  if (data && data.length > 0) {
                    await db.delete('calendar_entries', id)
                    await db.put('calendar_entries', {
                      ...data[0],
                      sync_status: 'synced'
                    })
                  }
                } else {
                  // Standardowa aktualizacja
                  const { error } = await supabase
                    .from('calendar_entries')
                    .update(updateData)
                    .eq('id', id)
                  
                  if (error) throw error
                  
                  // Zaktualizuj status w IndexedDB
                  const entry = await db.get('calendar_entries', id)
                  if (entry) {
                    await db.put('calendar_entries', {
                      ...entry,
                      ...updateData,
                      sync_status: 'synced'
                    })
                  }
                }
                break
              }
              
              case 'delete': {
                const { id } = change.data
                
                // Jeśli ID zaczyna się od 'offline-', to ten wpis nie został jeszcze dodany do serwera
                if (!id.startsWith('offline-')) {
                  const { error } = await supabase
                    .from('calendar_entries')
                    .delete()
                    .eq('id', id)
                  
                  if (error) throw error
                }
                
                // Usuń wpis z IndexedDB
                await db.delete('calendar_entries', id)
                break
              }
            }
          }
          
          // Usuń zmianę z pending_changes
          await db.delete('pending_changes', change.id)
          console.log(`✅ Zmiana ${change.id} zsynchronizowana i usunięta`)
        } catch (error) {
          console.error(`❌ Błąd synchronizacji zmiany ${change.id}:`, error)
          // Nie usuwamy zmiany, aby spróbować ponownie później
        }
      }
      
      console.log('🎉 Synchronizacja zakończona')
    } catch (error) {
      console.error('❌ Błąd podczas synchronizacji:', error)
    }
  }, [isOnline, user])
  
  // Nasłuchuj na zmiany statusu online
  useEffect(() => {
    if (isOnline) {
      console.log('🌐 Połączenie z internetem przywrócone, rozpoczynam synchronizację')
      syncToServer()
    }
  }, [isOnline, syncToServer])
  
  // Nasłuchuj na wiadomości od Service Worker
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SYNC_CALENDAR_ENTRIES') {
        console.log('📬 Otrzymano żądanie synchronizacji od Service Worker')
        syncToServer()
      }
    }
    
    navigator.serviceWorker?.addEventListener('message', handleMessage)
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage)
    }
  }, [syncToServer])
  
  return {
    syncToServer
  }
}